import React, { FC, SVGProps, useCallback, useEffect, useState } from 'react';
import { Alert } from 'antd';
import { KeyRound } from 'lucide-react';
import styles from './index.module.css';

import mcpChatGpt from '../../../../../assets/images/icons/integrations/ic-integration-mcp-chatgpt.svg';
import mcpClaude from '../../../../../assets/images/icons/integrations/ic-integration-mcp-claude.svg';
import mcpCline from '../../../../../assets/images/icons/integrations/ic-integration-mcp-cline.svg';
import mcpCursor from '../../../../../assets/images/icons/integrations/ic-integration-mcp-cursor.svg';
import mcpGemini from '../../../../../assets/images/icons/integrations/ic-integration-mcp-gemini.svg';
import mcpVsCode from '../../../../../assets/images/icons/integrations/ic-integration-mcp-vscode.svg';
import mcpWindsurf from '../../../../../assets/images/icons/integrations/ic-integration-mcp-windsurf.svg';
import mcpZed from '../../../../../assets/images/icons/integrations/ic-integration-mcp-zed.svg';
import mcpAntigravity from '../../../../../assets/images/icons/integrations/ic-integration-mcp-antigravity.png';

import type { SvgIconComponent } from '../../integration_settings/integrationsInfo';

const CLIENT_ICONS: Record<string, SvgIconComponent> = {
  Codex: mcpChatGpt,
  cursor: mcpCursor,
  mcp_chatgpt: mcpChatGpt,
  mcp_codexcli: mcpChatGpt,
  mcp_claudecode: mcpClaude,
  mcp_claudeai: mcpClaude,
  mcp_cline: mcpCline,
  mcp_cursor: mcpCursor,
  mcp_geminicli: mcpGemini,
  mcp_githubcopilot: mcpCursor,
  mcp_vscodecopilot: mcpVsCode,
  mcp_windsurf: mcpWindsurf,
  mcp_zed: mcpZed,
  mcp_antigravity: mcpAntigravity,
};

type AccessToken = {
  id: number;
  organization_id: number;
  organization_name: string;
  revoke_token: string;
  client_id: string;
  client_name: string;
  token_hint: string;
  scopes: string;
  created_at: string;
  updated_at: string;
};

type SvgIconWrapperProps = SVGProps<SVGSVGElement>;

type OrganizationOption = {
  id: number;
  name: string;
};

const ClientCell: FC<{ token: AccessToken }> = ({ token }) => {
  const Icon = CLIENT_ICONS[token.client_id];
  const iconSvgProps: SvgIconWrapperProps = {
    className: styles.clientIconSvg,
    'aria-hidden': true,
    role: 'presentation',
    focusable: false,
  };

  return (
    <div className={styles.clientCell}>
      {Icon ? (
        <Icon {...iconSvgProps} />
      ) : (
        <KeyRound className={styles.clientIconFallback} aria-hidden />
      )}
      <span>{token.client_name}</span>
    </div>
  );
};

const AccessTokens: FC<{ organizations: OrganizationOption[] }> = ({ organizations }) => {
  const [tokens, setTokens] = useState<AccessToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [revokeError, setRevokeError] = useState(false);
  const [revokingTokenId, setRevokingTokenId] = useState<number | null>(null);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('all');

  const loadTokens = useCallback(() => {
    setLoading(true);
    setLoadError(false);

    fetch('/settings/mcp_access_tokens_data', {
      headers: { Accept: 'application/json' },
    })
      .then(async res => {
        if (!res.ok) {
          throw new Error(`Failed to load access tokens (${res.status})`);
        }

        return res.json();
      })
      .then(data => {
        setTokens(data.tokens || []);
        setLoadError(false);
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  const handleRevoke = async (event: React.MouseEvent<HTMLAnchorElement>, token: AccessToken) => {
    event.preventDefault();
    if (revokingTokenId) return;

    setRevokeError(false);
    setRevokingTokenId(token.id);

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      const body = new URLSearchParams({
        token: token.revoke_token,
        token_type: 'access_token',
      }).toString();

      const response = await fetch('/api_mcp/oauth/revoke', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
        body,
      });

      if (!response.ok) {
        throw new Error(`Failed to revoke token (${response.status})`);
      }

      loadTokens();
    } catch {
      setRevokeError(true);
    } finally {
      setRevokingTokenId(null);
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString('sv-SE', { hour12: false }).replace('T', ' ');

  const filteredTokens =
    selectedOrganizationId === 'all'
      ? tokens
      : tokens.filter(token => token.organization_id === Number(selectedOrganizationId));

  const isEmpty = !loading && !loadError && filteredTokens.length === 0;

  return (
    <div className={styles.container}>
      <h2 className={styles.heading}>MCP Access tokens</h2>

      {organizations.length > 1 && (
        <div className={styles.filterRow}>
          <label htmlFor="access-token-org-filter" className={styles.filterLabel}>
            Organization
          </label>
          <select
            id="access-token-org-filter"
            className={styles.filterSelect}
            value={selectedOrganizationId}
            onChange={event => setSelectedOrganizationId(event.target.value)}
          >
            <option value="all">All organizations</option>
            {organizations.map(organization => (
              <option key={organization.id} value={organization.id}>
                {organization.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <table className={styles.table}>
        <thead>
          <tr>
            <th>Client</th>
            <th>Token</th>
            <th>Scopes</th>
            <th>Created</th>
            <th>Last updated</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {revokeError && (
            <tr>
              <td colSpan={6} className={styles.emptyState}>
                Unable to revoke access token right now.
              </td>
            </tr>
          )}
          {loadError && (
            <tr>
              <td colSpan={6} className={styles.emptyState}>
                Unable to load access tokens right now.
              </td>
            </tr>
          )}
          {isEmpty && (
            <tr>
              <td colSpan={6} className={styles.emptyState}>
                No access tokens yet.
              </td>
            </tr>
          )}
          {filteredTokens.map(token => (
            <tr key={token.id}>
              <td><ClientCell token={token} /></td>
              <td className={styles.tokenHint}>{token.token_hint}</td>
              <td>{token.scopes}</td>
              <td className={styles.createdAt}>{formatDate(token.created_at)}</td>
              <td className={styles.createdAt}>{formatDate(token.updated_at)}</td>
              <td>
                <span>
                  Active{' '}
                  <a
                    href="#"
                    className={styles.revokeLink}
                    onClick={event => handleRevoke(event, token)}
                    aria-disabled={revokingTokenId === token.id}
                  >
                    {revokingTokenId === token.id ? '(revoking...)' : '(revoke)'}
                  </a>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Alert
        className={styles.infoBanner}
        type="info"
        showIcon
        message={
          <>
            Connect BugHerd MCP to your AI tools from any project&apos;s Integrations
            Settings screen.
          </>
        }
      />
    </div>
  );
};

export default AccessTokens;
